import { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { Card, CardContent } from '@/components/ui/card';
import { Radio } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useAuthStore } from '@/lib/stores/auth';
import { useStreamsStore } from '@/lib/stores/streams';
import { StreamCard } from '@/components/streams/stream-card';
import { StreamDetailsSheet } from '@/components/streams/stream-details-sheet';
import FilterBar from '@/components/common/filter-bar';
import VirtualizedGrid from '@/components/common/virtualized-grid';
import { PageHeader } from '@/components/page/page-header';
import { PageSection } from '@/components/page/page-section';
import { type FilterConfig, type FilterOption } from '@/lib/shared-types/filters';
import type { StreamFilters } from '@/lib/shared-types';
import type { Stream } from '@/lib/stores/streams/types';

const BROADCAST_STATUS_VALUES = ['all', 'live', 'scheduled', 'ended'] as const;
const BROADCAST_VISIBILITY_VALUES = ['all', 'public', 'members', 'invite_only'] as const;

function isBroadcastStatus(value: string): value is NonNullable<StreamFilters['status']> {
  return (BROADCAST_STATUS_VALUES as readonly string[]).includes(value);
}

function isBroadcastVisibility(value: string): value is NonNullable<StreamFilters['visibility']> {
  return (BROADCAST_VISIBILITY_VALUES as readonly string[]).includes(value);
}

const broadcastVisibilityOptions: FilterOption[] = [
  { value: 'public', label: 'Public' },
  { value: 'all', label: 'All Accessible' },
  { value: 'members', label: 'Members' },
  { value: 'invite_only', label: 'Invite Only' },
];

function buildBroadcastFilters(isAuthenticated: boolean): FilterConfig[] {
  const filters: FilterConfig[] = [
    {
      id: 'searchQuery',
      label: 'Search',
      type: 'search',
      placeholder: 'Search broadcasts...',
    },
    {
      id: 'status',
      label: 'Status',
      type: 'select',
      options: [
        { value: 'live', label: 'Live' },
        { value: 'scheduled', label: 'Scheduled' },
        { value: 'ended', label: 'Ended' },
        { value: 'all', label: 'All Statuses' },
      ],
      placeholder: 'All Statuses',
    },
  ];

  if (isAuthenticated) {
    filters.push({
      id: 'visibility',
      label: 'Access',
      type: 'select',
      options: broadcastVisibilityOptions,
      placeholder: 'All Accessible',
    });
  }

  return filters;
}

export default function BroadcastPage() {
  const { isAuthenticated } = useAuthStore();
  const {
    streams,
    isLoading,
    filters,
    fetchStreams,
    setFilters,
    pagination,
  } = useStreamsStore();
  const [searchParams] = useSearchParams();
  const [selectedStream, setSelectedStream] = useState<Stream | null>(null);
  const loadMoreStreams = useStreamsStore.getState().loadMoreStreams;

  const broadcastFilters = useMemo(
    () => buildBroadcastFilters(isAuthenticated),
    [isAuthenticated]
  );

  useEffect(() => {
    const urlFilters: Record<string, string | string[]> = {};
    searchParams.forEach((value, key) => {
      if (value.includes(',')) {
        urlFilters[key] = value.split(',');
      } else {
        urlFilters[key] = value;
      }
    });

    const nextFilters: Partial<StreamFilters> = {
      ...urlFilters,
      status: typeof urlFilters.status === 'string' && isBroadcastStatus(urlFilters.status)
        ? urlFilters.status
        : (isAuthenticated ? 'all' : 'live'),
      visibility: typeof urlFilters.visibility === 'string' && isBroadcastVisibility(urlFilters.visibility)
        ? urlFilters.visibility
        : (isAuthenticated ? 'all' : 'public'),
      searchQuery: typeof urlFilters.searchQuery === 'string' ? urlFilters.searchQuery : '',
      sortBy: 'createdAt',
      sortOrder: 'desc',
      region: undefined,
      tags: undefined,
      creatorId: undefined,
    };

    setFilters(nextFilters);
  }, [isAuthenticated, searchParams, setFilters]);

  useEffect(() => {
    fetchStreams(filters);
  }, [fetchStreams, filters]);

  const handleFilterChange = useCallback(
    (newFilters: Record<string, string | string[]>) => {
      setFilters(newFilters as Partial<StreamFilters>);
    },
    [setFilters]
  );

  const visibleStreams = streams;

  return (
    <div className="@container/broadcast min-h-screen w-full max-w-[1800px] mx-auto bg-background px-3 py-4 @[740px]/broadcast:px-4 @[740px]/broadcast:py-5 @[1100px]/broadcast:px-6 @[1100px]/broadcast:py-6">
      <PageHeader
        title="Broadcast"
        badge={(
          <Badge variant="outline" className="border-primary/20 bg-primary/5">
            <Radio className="mr-1 h-3 w-3 text-primary" />
            Stream browser
          </Badge>
        )}
      />
      <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
        Browse live services, scheduled streams, and recordings with filters that follow your access.
      </p>

      <PageSection asCard={false} className="mt-6">
        <FilterBar
          filters={broadcastFilters}
          activeFilters={filters as Record<string, string | string[]>}
          onFilterChange={handleFilterChange}
          className="mb-6"
        />

        {isLoading && streams.length === 0 ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : visibleStreams.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Radio className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
              <h3 className="mb-2 text-lg font-semibold">No broadcasts available</h3>
              <p className="mb-4 text-muted-foreground">
                Check back soon for live streams and recorded content.
              </p>
              <Button variant="outline" onClick={() => void fetchStreams(filters)}>
                Refresh streams
              </Button>
            </CardContent>
          </Card>
        ) : (
          <VirtualizedGrid
            items={visibleStreams}
            renderItem={(stream) => (
              <StreamCard
                stream={stream}
                onDetailsClick={() => setSelectedStream(stream)}
              />
            )}
            keyExtractor={(stream) => String(stream.id)}
            onLoadNextPage={() => void loadMoreStreams()}
            hasNextPage={pagination.hasMore}
            loading={isLoading}
          />
        )}
      </PageSection>

      {selectedStream ? (
        <StreamDetailsSheet
          stream={selectedStream}
          open
          onOpenChange={(open) => !open && setSelectedStream(null)}
        />
      ) : null}
    </div>
  );
}
